import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PropsWithChildren } from 'react';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { investment as rules } from 'further-ui/validations';
import { FundingSource } from 'further-types/lp/investment';
import { AllocationRule } from 'further-types/lp/allocations';
import { ActionStatus } from 'further-types/lp/action';
import FormWithStateContextProvider from 'contexts/FormWithStateContext';
import { InvestmentRevaluationOption } from 'further-types/lp/investment';

const defaultHoldingClass = {
  isNewHoldingClass: false,
  holdingClassId: undefined,
  name: '',
  investmentAmountTcy: 0,
  numberOfUnits: 0,
  unitPriceTcy: 0,
  currency: undefined,
};

const defaultInvestment = {
  vehicleId: '',
  dealName: '',
  dealCompletionDate: undefined,
  fundingSource: FundingSource.CapitalCall,
  allocationRule: AllocationRule.ByCommitmentAndClosingDate,
  status: ActionStatus.Draft,
  allocations: [],
  transactions: [],
  holdingClasses: [defaultHoldingClass],
  isNewAsset: false,
  assetId: '',
};

export type InvestmentFormState = {
  showPortalInformation: boolean;
};

type Props = {
  values?: rules.CreateInvestmentFormValues;
  vehicleId: string;
  baseCurrency?: CurrencyCode;
  defaultFundingSource?: FundingSource;
};

const FormWrapper: React.FC<PropsWithChildren<Props>> = ({
  children,
  values,
  vehicleId,
  defaultFundingSource,
}) => {
  const formMethods = useForm<rules.CreateInvestmentFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(rules.CreateInvestmentSchema),
    defaultValues: values ?? {
      ...defaultInvestment,
      vehicleId,
      fundingSource: defaultFundingSource ?? FundingSource.CapitalCall,
      holdingClasses: [defaultHoldingClass],
      createdNewAsset: undefined,
      createdNewHoldingClass: undefined,
      revaluationOption: InvestmentRevaluationOption.AllShares,
    },
    criteriaMode: 'all',
    resetOptions: {
      keepDefaultValues: false,
    },
  });

  const initialState: InvestmentFormState = {
    showPortalInformation: false,
  };

  return (
    <FormWithStateContextProvider
      formMethods={formMethods}
      initialState={initialState}
    >
      {children}
    </FormWithStateContextProvider>
  );
};

export default FormWrapper;
